import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';

import Spinner from '~/components/Common/Spinner';

import { useCreateAmenityGroup } from '~/hooks/apis/amenities/useCreateAmenityGroup';

interface Props {
  isModalOpen: boolean;
  closeModal: () => void;
  refreshGroups: () => void;
}

export default function NewAmenityGroupModal({ isModalOpen, closeModal, refreshGroups }: Props) {
  const [name, setName] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const createAmenityGroupMutation = useCreateAmenityGroup();

  const handleSubmit = async () => {
    if (!name) {
      enqueueSnackbar('Please fill in all fields', { variant: 'error' });
      return;
    }

    try {
      await createAmenityGroupMutation.mutateAsync({ name });
      enqueueSnackbar('Amenity group created successfully', { variant: 'success' });
      refreshGroups();
      closeModal();
      setName('');
    } catch (error) {
      enqueueSnackbar((error as Error).message, { variant: 'error' });
    }
  };

  return (
    <Dialog open={isModalOpen} onClose={closeModal}>
      <DialogTitle>Create New Amenity Group</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          type="text"
          fullWidth
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Name"
        />
        <Typography sx={{ marginTop: 2 }}>New groups will be placed last in order</Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={closeModal} disabled={createAmenityGroupMutation.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={createAmenityGroupMutation.isPending}>
          {createAmenityGroupMutation.isPending ? <Spinner size={15} inline /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
